import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EmptyCard from '../../../components/common/Cards/emptyCard';
import empty from '../../../assets/images/empty.png';
import ProjectApi from '../project-api';
import StatusIndicator from '../../../components/common/StatusIndicator';
import { STATUS_CLASSNAMES } from '../../ClientContainer/constants';
import { ROLES } from '../../../utils/constants';
import Alert from '../../../components/common/Alert';
import Menu, { MenuContainer } from '../../../components/common/Menu';
import { useHistory } from 'react-router-dom';
import UsersAPI from '../../UserContainer/user-api';
import EnhancedTable from '../../../components/common/Table';
import Button from '../../../components/common/Button';
import ConfirmationModal from '../../ClientContainer/statusModal';
import useDebounce from '../../../hooks/useDebounce';

const ACTIONS = {
  INACTIVE: 'inactive',
  DELETED: 'deleted',
  ACTIVATE: 'activate',
};
const LIMIT_PER_PAGE = 25;

const MenteesTable = ({ project }) => {
  const history = useHistory();
  const [mentees, setMentees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState();
  const [isMenuOpen, setIsMenuOpen] = useState({ open: false, id: '' });
  const menuRef = React.useRef(null);
  const [isModalOpen, setIsModalOpen] = useState({
    open: false,
    message: '',
    title: '',
  });
  const [usersSelected, setUsersSelected] = useState([]);
  const [menteeSelected, setMenteeSelected] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);

  const loadMentees = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: {
          mentees: fetchedMentees = [],
          clientId: fetchedClientId,
          totalPages: fetchedTotalPages = 0,
          pageNumber = 1,
        },
      } = await ProjectApi.fetchUserByProject(
        project.announcementId,
        ROLES.Mentee,
        currentPage,
        LIMIT_PER_PAGE,
        debouncedSearchText
      );

      setClientId(fetchedClientId);
      setMentees(fetchedMentees);
      setTotalPages(fetchedTotalPages);

      if (pageNumber !== currentPage) {
        setCurrentPage(pageNumber);
      }
    } catch (fetchError) {
      setError(fetchError?.data?.message || 'No se pudo obtener el listado de mentees');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchText, project.announcementId]);

  useEffect(() => {
    loadMentees();
  }, [loadMentees]);

  const onClick = (id) => {
    setIsMenuOpen((prev) => ({ id, open: !prev.open }));
  };

  useEffect(() => {
    if (!isMenuOpen.open) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen({ open: false, id: '' });
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen.open]);

  const handleActivation = async ({ menteeId, status }) => {
    try {
      await UsersAPI.updateUser(menteeId, {
        status,
      });
      await loadMentees();
    } catch (updateError) {
      setError(updateError?.data?.message || 'No se pudo actualizar el mentee');
    } finally {
      setIsModalOpen({ open: false, message: '', title: '' });
    }
  };

  const onAccept = (action, menteeId) => {
    setIsMenuOpen({ open: false, id: '' });
    switch (action) {
      case ACTIONS.INACTIVE:
        handleActivation({ menteeId, status: 'deactivated' });
        break;
      case ACTIONS.ACTIVATE:
        handleActivation({ menteeId, status: 'active' });
        break;
      default:
        break;
    }
  };

  const handleChangeOfStatus = (action, menteeId) => {
    switch (action) {
      case ACTIONS.INACTIVE:
        setMenteeSelected({ mentee: menteeId, action: ACTIONS.INACTIVE });
        setIsModalOpen({
          open: true,
          message:
            'El usuario se inactivará, no podrá acceder a la plataforma o asistir a reuniones. ¿Estás seguro de inactivarlo?',
          title: 'Inactivación del usuario',
        });
        break;
      case ACTIONS.ACTIVATE:
        setMenteeSelected({ mentee: menteeId, action: ACTIONS.ACTIVATE });
        setIsModalOpen({
          open: true,
          message: '¿Estás seguro de activar al mentee?',
          title: 'Activar mentee',
        });
        break;
      default:
        break;
    }
  };

  const pdfDownload = async (id) => {
    try {
      const { data } = await UsersAPI.fetchPdfUser(id);
      window.open(data.link, '_blank');
    } catch (downloadError) {
      console.error('Error fetching PDF data:', downloadError.message);
      setError('Ocurrio un error al descargar el PDF');
    }
  };

  const diagnosticoDownload = async (id) => {
    try {
      setError('');
      await UsersAPI.downloadUserDiagnostico(id);
    } catch (downloadError) {
      setError(
        downloadError?.status === 404
          ? 'El usuario no tiene un diagnóstico disponible para descargar'
          : 'Ocurrió un error al descargar el diagnóstico'
      );
    }
  };

  const renderSignupState = (step) => {
    if (step === 0) {
      return 'Registro completo';
    } else if (step === 1) {
      return 'Registro Inicial';
    } else {
      return 'Registro Incompleto';
    }
  };

  const onClickApplication = async (isAccepted, users = []) => {
    try {
      await ProjectApi.updateUserApplication(project.announcementId, {
        isAccepted,
        users,
      });
      setUsersSelected([]);
      await loadMentees();
    } catch (requestError) {
      console.log(requestError);
    }
  };

  const detachUser = async (userId) => {
    try {
      await ProjectApi.deleteUserAnnouncement(project.announcementId, userId);
      await loadMentees();
    } catch (requestError) {
      console.log(requestError);
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'Nombre', accessor: 'name' },
      { Header: 'E-mail', accessor: 'email' },
      { Header: 'Celular', accessor: 'celphone' },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => (
          <StatusIndicator
            text={value}
            statusClassName={
              value === 'Activo'
                ? STATUS_CLASSNAMES.ACTIVE
                : STATUS_CLASSNAMES.INACTIVE
            }
          />
        ),
      },
      {
        Header: 'Estado Registro',
        Cell: ({ row: { original } }) => renderSignupState(original.registro),
      },
      {
        Header: 'Estado postulacion',
        accessor: 'isAccepted',
      },
      { Header: 'Pilar', accessor: 'pillar' },
      {
        Header: 'Diagnóstico',
        accessor: 'hasDiagnostico',
        Cell: ({ value, row: { original } }) =>
          value ? (
            <button
              type="button"
              onClick={() => diagnosticoDownload(original.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: '1px solid #4dabf5',
                color: '#1976d2',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                download
              </span>
              Descargar
            </button>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: 13 }}>Sin diagnóstico</span>
          ),
      },
      {
        Header: 'Acción',
        accessor: 'id',
        Cell: ({ value, row: { original } }) => (
          <MenuContainer onClick={() => onClick(value)}>
            {isMenuOpen.id === value && (
              <div ref={menuRef}>
                <Menu
                  open={isMenuOpen.open}
                  items={[
                    {
                      text: 'Agendar reunión',
                      action: () =>
                        history.push('/clientes/reunion/nueva', {
                          menteeId: value,
                          menteeName: original.name,
                          ref: original.referer,
                          announcementId: project.announcementId,
                          clientId,
                        }),
                    },
                    {
                      text: 'Editar',
                      action: () =>
                        history.push('/usuarios/editar', {
                          idUser: value,
                          roleType: ROLES.Mentee,
                          ref: original.referer,
                          announcementId: project.announcementId,
                          clientId,
                        }),
                    },
                    {
                      text: 'Aceptar',
                      action: () => {
                        onClickApplication(true, [original.id]);
                      },
                      status:
                        original.isAccepted === 'Aceptado'
                          ? 'disabled'
                          : 'featured',
                    },
                    {
                      text: 'Rechazar',
                      action: () => onClickApplication(false, [original.id]),
                      status:
                        original.isAccepted === 'Rechazado'
                          ? 'disabled'
                          : 'warning',
                    },
                    {
                      text: 'Liberar de convocatoria',
                      action: () => detachUser(original.id),
                      status: 'warning',
                    },
                    {
                      text:
                        original.status !== 'Activo' ? 'Activar' : 'Desactivar',
                      action: () =>
                        original.status === 'Activo'
                          ? handleChangeOfStatus(ACTIONS.INACTIVE, value)
                          : handleChangeOfStatus(ACTIONS.ACTIVATE, value),
                    },
                    {
                      text: 'Descargar PDF',
                      action: () => pdfDownload(original.id),
                    },
                    {
                      text: 'Descargar diagnóstico',
                      action: () => diagnosticoDownload(original.id),
                    },
                  ]}
                />
              </div>
            )}
          </MenuContainer>
        ),
      },
    ],
    [clientId, history, isMenuOpen, project.announcementId]
  );

  return (
    <div>
      {error && <Alert type="error">{error}</Alert>}
      {isLoading || mentees.length ? (
        <EnhancedTable
          data={mentees}
          columns={columns}
          isLoadingData={isLoading}
          tableName="Mentees"
          buttonLabel="Nuevo Participante"
          onClickButton={() => {
            history.push('/clientes/detalles/proyecto/agregar', {
              client: {
                id: clientId,
                name: project.client,
                status: project.clientStatus,
              },
              announcement: {
                id: project.announcementId,
                title: project.title,
              },
              role: 'mentee',
            });
          }}
          onChangePage={(event, newPage) => setCurrentPage(newPage)}
          currentPage={currentPage}
          totalPages={totalPages}
          onSearch={(event) => {
            setCurrentPage(1);
            setSearchText(event.target.value || '');
          }}
          isDynamic
        />
      ) : (
        <div className="empty-tab">
          <EmptyCard
            message="Actualmente no existen mentees registrados"
            img={empty}
          >
            <Button
              className="actions-table__button button"
              type="button"
              onClick={() => {
                history.push('/clientes/detalles/proyecto/agregar', {
                  client: {
                    id: clientId,
                    name: project.client,
                    status: project.clientStatus,
                  },
                  announcement: {
                    id: project.announcementId,
                    title: project.title,
                  },
                  role: 'mentee',
                });
              }}
            >
              <span className="material-symbols-outlined">add</span>
              Crear nuevo usuario
            </Button>
          </EmptyCard>
        </div>
      )}
      <ConfirmationModal
        title={isModalOpen.message}
        open={isModalOpen.open}
        header={isModalOpen.title}
        handleDenied={() =>
          setIsModalOpen({ open: false, message: '', title: '' })
        }
        handleAccept={() => {
          onAccept(menteeSelected.action, menteeSelected.mentee);
        }}
        onClose={() => setIsModalOpen({ open: false, message: '', title: '' })}
      />
    </div>
  );
};

export default MenteesTable;
